/**
 * VideoEmbed — affiche la vidéo YouTube ou Vimeo citée dans un post.
 *
 * Deux modes :
 *   compact  → miniature seule, non interactive (fil : c'est la carte entière
 *              qui est cliquable, on n'imbrique pas de bouton dans le <Link>)
 *   complet  → miniature cliquable, puis lecteur intégré au clic
 *
 * Le lecteur n'est chargé qu'APRÈS le clic : aucun script YouTube/Vimeo,
 * aucun cookie tiers tant que l'utilisateur ne lance pas la lecture.
 * (youtube-nocookie + dnt=1 côté Vimeo.)
 */
import { useEffect, useState } from "react";
import { Play } from "lucide-react";
import {
  PROVIDER_LABEL,
  embedUrl,
  fetchVimeoThumbnail,
  watchUrl,
  youtubeThumbnail,
} from "../lib/media";

export default function VideoEmbed({ video, compact = false, testid }) {
  const [thumb, setThumb] = useState(
    video?.provider === "youtube" ? youtubeThumbnail(video.videoId) : null
  );
  const [playing, setPlaying] = useState(false);

  const provider = video?.provider;
  const videoId = video?.videoId;

  useEffect(() => {
    if (provider !== "vimeo" || !videoId) return undefined;
    const ctrl = new AbortController();
    let alive = true;
    fetchVimeoThumbnail(videoId, { signal: ctrl.signal }).then((url) => {
      if (alive && url) setThumb(url);
    });
    return () => {
      alive = false;
      ctrl.abort();
    };
  }, [provider, videoId]);

  if (!video) return null;

  const label = PROVIDER_LABEL[provider] || "Vidéo";

  // --- Lecteur ---------------------------------------------------------------
  if (playing && !compact) {
    return (
      <div
        className="relative w-full overflow-hidden rounded-[12px] bg-black"
        style={{ aspectRatio: "16 / 9", border: "1px solid #E5E5E5" }}
        data-testid={testid || "video-player"}
      >
        <iframe
          src={embedUrl(video, { autoplay: true })}
          title={`Vidéo ${label}`}
          className="absolute inset-0 w-full h-full"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }

  // --- Miniature -------------------------------------------------------------
  const Thumb = (
    <div
      className="relative w-full overflow-hidden rounded-[12px] bg-[#111]"
      style={{ aspectRatio: "16 / 9", border: "1px solid #E5E5E5" }}
    >
      {thumb ? (
        <img
          src={thumb}
          alt={`Miniature de la vidéo ${label}`}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover"
          // maxresdefault n'existe pas pour toutes les vidéos YouTube :
          // on retombe silencieusement sur hqdefault, universel.
          onError={() => {
            if (provider === "youtube") {
              const fallback = youtubeThumbnail(videoId).replace(
                "maxresdefault",
                "hqdefault"
              );
              setThumb((cur) => (cur === fallback ? cur : fallback));
            }
          }}
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center text-xs text-white/60">
          {label}
        </div>
      )}

      <div className="absolute inset-0 flex items-center justify-center">
        <span
          className="flex items-center justify-center rounded-full shadow-lg"
          style={{ width: 56, height: 56, background: "rgba(255,255,255,0.92)" }}
        >
          <Play size={22} color="#F77F00" fill="#F77F00" style={{ marginLeft: 3 }} />
        </span>
      </div>

      <span
        className="absolute bottom-2 right-2 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider"
        style={{ background: "rgba(0,0,0,0.65)", color: "#FFF" }}
      >
        {label}
      </span>
    </div>
  );

  if (compact) {
    return (
      <div data-testid={testid || "video-thumb"} aria-label={`Vidéo ${label}`}>
        {Thumb}
      </div>
    );
  }

  return (
    <div data-testid={testid || "video-embed"}>
      <button
        type="button"
        onClick={() => setPlaying(true)}
        className="block w-full text-left active:scale-[0.99] transition-transform"
        aria-label={`Lire la vidéo ${label}`}
        data-testid="video-play"
      >
        {Thumb}
      </button>
      <a
        href={watchUrl(video)}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-block mt-1.5 text-xs underline underline-offset-2"
        style={{ color: "#666" }}
      >
        Ouvrir sur {label}
      </a>
    </div>
  );
}
